import React, { useMemo, useState, useContext, useEffect } from 'react';
import PageWrapper from '../../../../layout/PageWrapper/PageWrapper';
import SubHeader, { SubHeaderLeft } from '../../../../layout/SubHeader/SubHeader';
import Page from '../../../../layout/Page/Page';
import Breadcrumb from '../../../../components/bootstrap/Breadcrumb';
import Button from '../../../../components/bootstrap/Button';
import Card, { CardBody, CardHeader, CardTitle } from '../../../../components/bootstrap/Card';
import TableCustom from '../../../../components/MyCustom/TableCustom';
import SelectEstablishment from '../../../../components/MyCustom/SelectEstablishment';
import SelectStore from '../../../../components/MyCustom/SelectStore';
import ReportModal from './ReportModal';
import Popovers from '../../../../components/bootstrap/Popovers';
import { DefaultContext } from '../../../../contexts/default';
import BillingChargesDB from '../../../../database/wrappers/billingCharges';
import BillingScheduleDB from '../../../../database/wrappers/billingSchedule';
import StoreDB from '../../../../database/wrappers/store';
import { where } from 'firebase/firestore';
import Money from '../../../../helpers/utils/money/money';
import showNotification from '../../../../components/extras/showNotification';
import Spinner from '../../../../components/bootstrap/Spinner';

const months = [
  'Jan', 'Fev', 'Mar', 'Abr', 'mai', 'Jun',
  'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'
];

const SignatureSubscription = () => {
  const { estabSelected, storeSelected } = useContext(DefaultContext);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [selectedCharge, setSelectedCharge] = useState(null);

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleRefresh = () => {
    setRefreshKey(oldKey => oldKey + 1);
  };

  useEffect(() => {
    if (!estabSelected) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const storeDB = new StoreDB(estabSelected);
        const stores = await storeDB.getAll();
        const storeMap = stores.reduce((acc, store) => {
          acc[store.id] = store.name;
          return acc;
        }, {});

        const billingDB = new BillingChargesDB();

        let constraints = [where('estabId', '==', estabSelected)];
        if (storeSelected) {
          constraints.push(where('storeId', '==', storeSelected));
        }

        let charges = await billingDB.getAll(...constraints);

        const scheduleDB = new BillingScheduleDB();
        let scheduleConstraints = [where('estabId', '==', estabSelected)];
        if (storeSelected) {
          scheduleConstraints.push(where('storeId', '==', storeSelected));
        }
        const schedules = await scheduleDB.getAll(...scheduleConstraints);

        const scheduleMap = schedules.reduce((acc, schedule) => {
          acc[schedule.storeId] = schedule;
          return acc;
        }, {});

        charges.sort((a, b) => {
          const dateA = a.created_at ? a.created_at.toDate() : new Date(0);
          const dateB = b.created_at ? b.created_at.toDate() : new Date(0);
          return dateB - dateA;
        });

        const newRows = charges.map(charge => {
          const status = mapStatus(charge.status);
          const schedule = scheduleMap[charge.storeId];

          let currentChargeDate = '--';
          let monthName = '--';
          let year = '--';

          if (charge.created_at) {
            const date = charge.created_at.toDate();
            monthName = months[date.getMonth()];
            year = date.getFullYear();
          }

          const getFormattedDate = (dayValue, baseDateObj) => {
            if (!dayValue) return '--';

            let day;
            if (dayValue && typeof dayValue === 'object' && typeof dayValue.toDate === 'function') {
              day = dayValue.toDate().getDate();
            } else {
              day = parseInt(dayValue);
            }

            if (!day || isNaN(day)) return '--';

            const d = new Date(baseDateObj);
            const year = d.getFullYear();
            const month = d.getMonth();
            const lastDay = new Date(year, month + 1, 0).getDate();
            const finalDay = Math.min(day, lastDay);

            return new Date(year, month, finalDay).toLocaleDateString('pt-BR');
          };

          if (charge.dueDate) {
            currentChargeDate = charge.dueDate.toDate().toLocaleDateString('pt-BR');
          } else if (schedule && charge.created_at) {
            currentChargeDate = getFormattedDate(schedule.currentChargeDate, charge.created_at.toDate());
          }

          return {
            id: charge.id,
            data: `${monthName}. ${year}`,
            vencimento: currentChargeDate,
            pagamento: charge.status === 'PAID' ? (charge.updated_at ? charge.updated_at.toDate().toLocaleDateString('pt-BR') : '--') : '--',
            assinatura: 1,
            pos: charge.items.pos?.qtdInitial || 0,
            posDelivery: charge.items.posDelivery?.qtdInitial > 0 ? 'Ativo' : 'Inativo',
            totem: charge.items.totem?.qtdInitial || 0,
            fiscal: charge.fiscalQtd,
            locacao: Money.centsToMaskMoney(charge.items.totem?.totalFinal || 0),
            desconto: Money.centsToMaskMoney(charge.totalDiscount),
            totalLiquido: Money.centsToMaskMoney(charge.totalInitial),
            imposto: Money.centsToMaskMoney(0),
            total: Money.centsToMaskMoney(charge.totalToPay),
            status: status,
            original: charge
          };
        });

        setRows(newRows);

      } catch (error) {
        let errorMessage = 'Não foi possível carregar as informações de assinatura. Tente novamente mais tarde.';
        let errorTitle = 'Erro ao buscar dados';

        if (error.code === 'failed-precondition' && error.message.includes('index')) {
          errorMessage = 'O sistema requer a criação de um índice no banco de dados para realizar esta consulta. Verifique o console para o link de criação.';
          errorTitle = 'Configuração necessária';
        }

        showNotification(
          errorTitle,
          errorMessage,
          'danger'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [estabSelected, storeSelected, refreshKey]);

  const mapStatus = (status) => {
    if (status === 'PENDING') return 'Pendente';
    if (status === 'PAID') return 'Pago';
    if (status === 'CANCELED') return 'Vencida';
    if (status === 'GENERATED') return 'Pendente';
    return status;
  };

  const columns = useMemo(() => [
    { label: 'DATA', field: 'data' },
    { label: 'VENCIMENTO', field: 'vencimento' },
    { label: 'PAGAMENTO', field: 'pagamento' },
    { label: 'ASSINATURA', field: 'assinatura' },
    { label: 'POS', field: 'pos' },
    {
      label: 'POS DELIVERY',
      field: 'posDelivery',
      format: row => (
        <Button
          isLight
          color={row.posDelivery === 'Ativo' ? 'success' : 'danger'}
          icon='Circle'
          className='text-nowrap'
          size="sm"
        >
          {row.posDelivery}
        </Button>
      )
    },
    { label: 'TOTEM', field: 'totem' },
    { label: 'FISCAL', field: 'fiscal' },
    { label: 'DESCONTO', field: 'desconto' },
    { label: 'TOTAL LIQUIDO', field: 'totalLiquido' },
    { label: 'TOTAL', field: 'total' },
    {
      label: 'STATUS',
      field: 'status',
      format: row => {
        let color = 'success';
        let icon = 'Savings';

        if (row.status === 'Pendente') {
          color = 'warning';
          icon = 'AccessTime';
        }
        if (row.status === 'Vencida') {
          color = 'danger';
          icon = 'Warning';
        }

        return (
          <Button
            isLight
            color={color}
            icon={icon}
            className='text-nowrap'
            size="sm"
          >
            {row.status}
          </Button>
        )
      }
    },
    {
      label: 'AÇÃO',
      field: 'actions',
      format: row => (
        row.status !== 'Pago' && (
          <div className='d-flex gap-2'>
            <Popovers trigger='hover' desc={'Boleto'}>
              <Button
                color="dark"
                icon="AttachMoney"
                size="sm"
                shadow="sm"
                onClick={() => {
                  if (row.original.url) {
                    window.open(row.original.url, '_blank');
                  } else {
                    showNotification(
                      'Aviso',
                      'Link de pagamento indisponível',
                      'warning'
                    );
                  }
                }}
              />
            </Popovers>
            <Popovers trigger='hover' desc={'Reportar erro'}>
              <Button color="danger" icon="Warning" size="sm" shadow="sm"
                onClick={() => {
                  setSelectedCharge(row.original);
                  setIsReportModalOpen(true);
                }} />
            </Popovers>
          </div>
        )
      ),
    }
  ], []);

  return (
    <PageWrapper title="Assinatura">
      <SubHeader>
        <SubHeaderLeft>
          <Breadcrumb
            list={[
              { title: 'Assinatura', to: '/subscription/signature' },
            ]}
          />
        </SubHeaderLeft>
      </SubHeader>
      <Page container='fluid'>
        <Card>
          <CardHeader className="mb-0 pb-0">
            <CardTitle>
              <h3>Assinatura</h3>
            </CardTitle>
          </CardHeader>
          <CardBody>
            <div className="row d-flex align-items-end mb-3">
              <SelectEstablishment class="col-md-3" />
              <SelectStore class="col-md-3" />
            </div>
            {loading ? (
              <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <Spinner color="primary" />
              </div>
            ) : (
              <TableCustom
                title=""
                columns={columns}
                rows={rows}
                fileName={'Assinatura'}
              />
            )}
          </CardBody>
        </Card>
      </Page>
      <ReportModal
        isOpen={isReportModalOpen}
        setIsOpen={setIsReportModalOpen}
        data={selectedCharge}
        onSuccess={handleRefresh}
      />
    </PageWrapper>
  );
};

export default SignatureSubscription;
